import React, { Suspense, lazy, useEffect, useState } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { refreshAccountAccess, validateToken } from "./store/slices/authSlice";
import { tokenManager } from "./utils/tokenManager";
import { canAccessAdministration } from "./utils/appRole";
import { ToastProvider, useToast } from "./contexts/ToastContext";
import { FooterProvider } from "./contexts/FooterContext";
import ToastContainer from "./components/ToastContainer";
import ProtectedRoute, { AccessDenied } from "./components/ProtectedRoute";
import LoginPage from "./pages/LoginPage";
import ActivateLicensePage from "./pages/ActivateLicensePage";
import DashboardLayout from "./components/DashboardLayout";
import { Toaster } from "react-hot-toast";
import Check from "@mui/icons-material/Check";
import WarningIcon from "@mui/icons-material/Warning";
import InfoIcon from "@mui/icons-material/Info";
import { AlertDataProvider } from "./contexts/AlertDataContext";
import { fetchLicenseStatus } from "./services/licenseApi";

/**
 * Route guard pattern (OBS-002):
 * - ProtectedRoute + moduleKey — module read from GET /api/account/access
 * - AdminRoute — administration shell (administrationSections / isAdmin)
 * - PermissionWrapper — in-page conditional UI (same appRole rules)
 */

const Users = lazy(() => import("./pages/UserManagement/Users"));
const AIDetectionDashboard = lazy(() =>
  import("./components/aiDetectionSections/AIDetectionDashboard.tsx"),
);
const MainAlertServices = lazy(() =>
  import("./pages/AlertServices/mainAlertServices"),
);
const Wahankhede360Page = lazy(() =>
  import("./pages/Place360/Wahankhede360Page.tsx"),
);
const FlashbackDashboard = lazy(() => import("./pages/FlashBack/Dashboard.tsx"));
const CameraPlaybackPage = lazy(() =>
  import("./pages/FlashBack/CameraPlaybackPage.tsx"),
);
const AdministrationLayout = lazy(() =>
  import("./pages/administration/AdministrationLayout"),
);
const AdministrationCameraManagement = lazy(() =>
  import("./pages/administration/AdministrationCameraManagement.tsx").then(
    (m) => ({ default: m.AdministrationCameraManagement }),
  ),
);
const AdministrationZoneManagement = lazy(() =>
  import("./pages/administration/AdministrationZoneManagement.tsx").then(
    (m) => ({ default: m.AdministrationZoneManagement }),
  ),
);
const AdministrationAiModelsManagement = lazy(() =>
  import("./pages/administration/AdministrationAiModelsSection.tsx").then(
    (m) => ({ default: m.AdministrationAiModelsManagement }),
  ),
);
const AdministrationRecycleBinManagement = lazy(() =>
  import("./pages/administration/AdministrationRecycleBinManagement.tsx").then(
    (m) => ({ default: m.AdministrationRecycleBinManagement }),
  ),
);
const UserRoleAdminPanel = lazy(() =>
  import("./features/userRoleAdmin/UserRoleAdminPanel.tsx").then((m) => ({
    default: m.UserRoleAdminPanel,
  })),
);
const NvrManagementPage = lazy(() => import("./pages/Nvr/NvrManagementPage.tsx"));

function RouteFallback() {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
      }}
    >
      Loading…
    </div>
  );
}

/** Administration routes — only when signed-in user may access Administration. */
function AdminRoute({ children }) {
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const accountAccess = useSelector((state) => state.auth.accountAccess);
  const accessLoaded = useSelector((state) => state.auth.accessLoaded);
  const loading = useSelector((state) => state.auth.loading);
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (loading || !accessLoaded) {
    return <RouteFallback />;
  }
  if (!canAccessAdministration(accountAccess)) {
    return <Navigate to="/omni-watch" replace />;
  }
  return children;
}

/** Pill toast with solid circular badge + icon (matches product toast reference). */
function ToastSuccessIcon() {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 22,
        height: 22,
        minWidth: 22,
        borderRadius: "50%",
        backgroundColor: "#2E7D32",
        flexShrink: 0,
      }}
      aria-hidden
    >
      <Check style={{ fontSize: 15, color: "#fff" }} />
    </span>
  );
}

function ToastErrorIcon() {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 22,
        height: 22,
        minWidth: 22,
        borderRadius: "20%",
        backgroundColor: "#C62828",
        flexShrink: 0,
        color: "#fff",
        fontSize: 13,
        fontWeight: 700,
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
        lineHeight: 1,
      }}
      aria-hidden
    >
      !
    </span>
  );
}

function ToastWarningIcon() {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 22,
        height: 22,
        minWidth: 22,
        borderRadius: "50%",
        backgroundColor: "#E65100",
        flexShrink: 0,
      }}
      aria-hidden
    >
      <WarningIcon style={{ fontSize: 15, color: "#fff" }} />
    </span>
  );
}

function ToastInfoIcon() {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 22,
        height: 22,
        minWidth: 22,
        borderRadius: "50%",
        backgroundColor: "#1565C0",
        flexShrink: 0,
      }}
      aria-hidden
    >
      <InfoIcon style={{ fontSize: 15, color: "#fff" }} />
    </span>
  );
}

// Suppress only the known harmless ResizeObserver loop message (not other errors)
const RESIZE_OBSERVER_LOOP =
  "ResizeObserver loop completed with undelivered notifications";
const isResizeObserverLoopMessage = (value) => {
  if (typeof value === "string") return value.includes(RESIZE_OBSERVER_LOOP);
  if (value && typeof value.message === "string") {
    return value.message.includes(RESIZE_OBSERVER_LOOP);
  }
  return false;
};
const originalError = console.error;
console.error = (...args) => {
  if (isResizeObserverLoopMessage(args[0])) {
    return;
  }
  originalError.apply(console, args);
};

// Suppress ResizeObserver errors in React error overlay
window.addEventListener("error", (event) => {
  if (
    event.message?.includes(
      "ResizeObserver loop completed with undelivered notifications",
    )
  ) {
    event.preventDefault();
    event.stopPropagation();
    return false;
  }
});

// Suppress ResizeObserver errors in unhandled promise rejections
window.addEventListener("unhandledrejection", (event) => {
  if (
    event.reason?.message?.includes(
      "ResizeObserver loop completed with undelivered notifications",
    )
  ) {
    event.preventDefault();
    return false;
  }
});

/**
 * App Content Component
 * Contains the main app content with toast functionality
 */
function AppContent() {
  const dispatch = useDispatch();
  const { isAuthenticated, loading, accessLoaded } = useSelector(
    (state) => state.auth,
  );
  const { toasts, removeToast } = useToast();

  const [licenseLoading, setLicenseLoading] = useState(true);
  const [licenseNeedsActivation, setLicenseNeedsActivation] = useState(false);
  const [licenseCheckError, setLicenseCheckError] = useState("");

  useEffect(() => {
    let cancelled = false;

    async function checkLicense() {
      setLicenseLoading(true);
      setLicenseCheckError("");
      try {
        const status = await fetchLicenseStatus();
        if (cancelled) return;
        // Prefer activated — never block login when the server says licensed.
        setLicenseNeedsActivation(!status.activated);
      } catch (err) {
        if (cancelled) return;
        // If status cannot be reached, block with activate screen so the user
        // can still try activation (or see a clear error on submit).
        setLicenseNeedsActivation(true);
        setLicenseCheckError(
          err?.message || "Unable to verify license status. Check that the API is running.",
        );
      } finally {
        if (!cancelled) setLicenseLoading(false);
      }
    }

    checkLicense();
    return () => {
      cancelled = true;
    };
  }, []);

  // Restore session only after license gate finishes — and never while activation is required.
  // Skip refresh when login/validateToken already loaded access (avoids duplicate GET /access).
  useEffect(() => {
    if (licenseLoading || licenseNeedsActivation) return;
    if (!tokenManager.isAuthenticated()) return;

    if (!isAuthenticated) {
      dispatch(validateToken());
      return;
    }

    if (accessLoaded) return;

    dispatch(refreshAccountAccess());
  }, [
    dispatch,
    isAuthenticated,
    accessLoaded,
    licenseNeedsActivation,
    licenseLoading,
  ]);

  if (licenseLoading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          fontSize: "18px",
        }}
      >
        Loading...
      </div>
    );
  }

  if (licenseNeedsActivation) {
    return (
      <div className="App">
        {licenseCheckError ? (
          <div
            role="alert"
            style={{
              position: "fixed",
              top: 16,
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 1000,
              background: "#FFEBEE",
              color: "#B71C1C",
              padding: "10px 16px",
              borderRadius: 8,
              maxWidth: "min(90vw, 480px)",
              fontSize: 14,
            }}
          >
            {licenseCheckError}
          </div>
        ) : null}
        <ActivateLicensePage
          onActivated={() => {
            setLicenseCheckError("");
            setLicenseNeedsActivation(false);
          }}
        />
      </div>
    );
  }

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          fontSize: "18px",
        }}
      >
        Loading...
      </div>
    );
  }

  return (
    <div className="App">
      <Toaster
        position="top-right"
        gutter={12}
        toastOptions={{
          duration: 4000,
          style: {
            background: "#f5f5f5",
            color: "#212121",
            borderRadius: "9999px",
            padding: "10px 18px 10px 12px",
            fontSize: "14px",
            fontWeight: 500,
            fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
            letterSpacing: "0.01em",
            boxShadow:
              "0 4px 18px rgba(0,0,0,0.1), 0 2px 6px rgba(0,0,0,0.06)",
            maxWidth: "min(calc(100vw - 32px), 440px)",
          },

          success: {
            style: {
              background: "#E8F5E9",
              color: "#1B5E20",
            },
            icon: <ToastSuccessIcon />,
          },

          error: {
            style: {
              background: "#FFEBEE",
              color: "#B71C1C",
            },
            icon: <ToastErrorIcon />,
          },

          warning: {
            style: {
              background: "#FFF3E0",
              color: "#E65100",
            },
            icon: <ToastWarningIcon />,
          },

          info: {
            style: {
              background: "#E3F2FD",
              color: "#0D47A1",
            },
            icon: <ToastInfoIcon />,
          },
        }}
      />

      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route
            path="/login"
            element={
              isAuthenticated ? <Navigate to="/omni-watch" /> : <LoginPage />
            }
          />

          <Route
            path="/user-management/users"
            element={
              <ProtectedRoute moduleKey="user_management">
                <DashboardLayout>
                  <Users />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/live-monitoring"
            element={
              <ProtectedRoute moduleKey="ai_detection">
                <DashboardLayout>
                  <AIDetectionDashboard />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/ai-analysis"
            element={
              <ProtectedRoute moduleKey="alert_service">
                <AlertDataProvider>
                  <DashboardLayout>
                    <MainAlertServices hideAlertRules />
                  </DashboardLayout>
                </AlertDataProvider>
              </ProtectedRoute>
            }
          />

          <Route
            path="/omni-watch"
            element={
              <ProtectedRoute moduleKey="omniwatch">
                <DashboardLayout>
                  <Wahankhede360Page />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/flashback"
            element={
              <ProtectedRoute moduleKey="flashback">
                <DashboardLayout>
                  <FlashbackDashboard />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/cameras/:id/playback"
            element={
              <ProtectedRoute moduleKey="flashback">
                <DashboardLayout>
                  <CameraPlaybackPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/administration"
            element={
              <AdminRoute>
                <DashboardLayout>
                  <AdministrationLayout />
                </DashboardLayout>
              </AdminRoute>
            }
          >
            <Route index element={<Navigate to="camera-mgmt" replace />} />
            <Route
              path="camera-mgmt"
              element={<AdministrationCameraManagement />}
            />
            <Route
              path="zone-mgmt"
              element={<AdministrationZoneManagement />}
            />
            <Route
              path="ai-models-mgmt"
              element={<AdministrationAiModelsManagement />}
            />
            <Route
              path="recycle-bin-mgmt"
              element={<AdministrationRecycleBinManagement />}
            />
            <Route
              path="alert-rules-mgmt"
              element={
                <Navigate
                  to="/administration/ai-models-mgmt?tab=alert-rules"
                  replace
                />
              }
            />
            <Route path="user-mgmt" element={<UserRoleAdminPanel />} />
            <Route path="nvr-mgmt" element={<NvrManagementPage />} />
          </Route>

          <Route path="/" element={<Navigate to="/omni-watch" />} />

          <Route
            path="*"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <AccessDenied message="The page you're looking for doesn't exist or you don't have permission to access it." />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
        </Routes>
      </Suspense>
      <ToastContainer toasts={toasts} onRemoveToast={removeToast} />
    </div>
  );
}

/**
 * Main application component that handles routing and authentication
 */
function App() {
  return (
    <ToastProvider>
      <FooterProvider>
        <AppContent />
      </FooterProvider>
    </ToastProvider>
  );
}

export default App;
